import type { ReactNode } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface OfflineGuardProps {
  children: ReactNode;
}

export function OfflineGuard({ children }: OfflineGuardProps) {
  const { isOnline, isChecking, checkNow } = useNetworkStatus();
  const { t } = useI18n();

  if (!isOnline) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
        <div className="card max-w-md w-full p-8 text-center space-y-6 border border-accent/30 bg-bg-raised shadow-xl animate-in fade-in zoom-in-95 duration-200">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto text-accent shadow-inner">
            <WifiOff size={32} />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-bold tracking-tight text-text-primary">
              {t('offline.title')}
            </h2>
            <p className="text-sm text-text-secondary leading-relaxed">
              {t('offline.message')}
            </p>
          </div>

          <div className="space-y-3 pt-2">
            <button
              onClick={() => checkNow()}
              disabled={isChecking}
              className="w-full btn btn-ghost flex items-center justify-center gap-2 py-2 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <RefreshCw size={14} className={isChecking ? 'animate-spin' : ''} />
              <span>{isChecking ? t('common.loading') : t('common.retry')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
